import React from 'react';
import { useData, useField } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { TextField, OptionField, AutoField, NumericField, DateField, StaticField, PickerModal, Section, ViewLayout } from '../EpcrFields';

const AGE_UNITS_YEARS = "2516009"; // ePatient.16 enumeration value for "Years"

// Accepts either the ISO format the "date" baseType actually expects
// (YYYY-MM-DD) or the MM/DD/YYYY format the field's placeholder suggests.
function parseDob(raw) {
  if (!raw) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return null;
}

function ageInYears(dob) {
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const beforeBirthdayThisYear = now.getMonth() < dob.getMonth()
    || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  return beforeBirthdayThisYear ? age - 1 : age;
}

export function EpcrViewPatient() {
  const data = useData();

  // Selecting a Home City auto-fills Home County from the same GNIS record
  // (City.json carries each city's county FIPS code) — county stays a plain,
  // still-editable text field for cities outside the demo's curated list.
  const handleCityPick = (cityCode) => {
    const entry = data.options("ePatient.06").find(o => o.value === cityCode);
    if (entry?.countyFips) data.setReportPath("ePatient/ePatient.07", entry.countyFips);
  };

  // Age Units has no UI of its own — every path that sets Age also silently
  // defaults its units to Years (data.path, not a hardcoded string: Age/Age
  // Units live under ePatient.AgeGroup, a nesting this must account for).
  const setAgeUnitsYears = () => {
    const p = data.path("ePatient.16");
    if (p) data.setReportPath(p, AGE_UNITS_YEARS);
  };

  // Fires once DOB entry is finished (on blur, not per-keystroke).
  const handleDobBlur = (raw) => {
    const dob = parseDob(raw);
    if (!dob || isNaN(dob.getTime())) return;
    const age = ageInYears(dob);
    if (age < 0 || age > 120) return;
    const agePath = data.path("ePatient.15");
    if (agePath) data.setReportPath(agePath, age);
    setAgeUnitsYears();
  };

  const sections = [
    { id: "demographics", label: "Demographics" },
    { id: "contact", label: "Contact" },
    { id: "history", label: "Medical History" },
    { id: "allergies", label: "Allergies" },
    { id: "medications", label: "Medications" },
    { id: "belongings", label: "Belongings" },
  ];

  return (
    <ViewLayout sections={sections}>

      <Section id="demographics" num="01" title="Demographics" meta="ePatient.*">
        <div className="fgrid">
          <div className="fcol-12"><TextField label="First Name" elem="ePatient.03" /></div>
          <div className="fcol-12"><TextField label="Middle Initial/Name" elem="ePatient.04" /></div>
          <div className="fcol-12"><TextField label="Last Name" elem="ePatient.02" /></div>
          <div className="fcol-12"><OptionField label="Sex" elem="ePatient.25" /></div>
          <div className="fcol-12"><OptionField label="Race" elem="ePatient.14" /></div>
          <div className="fcol-12">
            <DateField label="Date of Birth" elem="ePatient.17" onBlur={handleDobBlur} />
          </div>
          <div className="fcol-12">
            <NumericField label="Age" elem="ePatient.15" onPick={setAgeUnitsYears} />
          </div>
          <div className="fcol-12"><TextField label="Social Security Number" elem="ePatient.12" placeholder="•••••••••" /></div>
          <div className="fcol-12"><AutoField label="Preferred Language(s)" elem="ePatient.24" /></div>
        </div>
      </Section>

      <Section id="contact" num="02" title="Contact Information" meta="ePatient.05–19">
        <div className="fgrid">
          <div className="fcol-12"><TextField label="Patient's Home Address" elem="ePatient.05" placeholder="Street address" /></div>
          <div className="fcol-12"><TextField label="Patient's Home ZIP Code" elem="ePatient.09" /></div>
          <div className="fcol-12"><OptionField label="Patient's Home City" elem="ePatient.06" onPick={handleCityPick} /></div>
          <div className="fcol-12"><TextField label="Patient's Home County" elem="ePatient.07" /></div>
          <div className="fcol-12"><OptionField label="Patient's Home State" elem="ePatient.08" /></div>
          <div className="fcol-12"><TextField label="Patient's Country of Residence" elem="ePatient.10" /></div>
          <div className="fcol-12"><TextField label="Patient's Phone Number" elem="ePatient.18" placeholder="(555) 000-0000" /></div>
          <div className="fcol-12"><TextField label="Patient's Email Address" elem="ePatient.19" /></div>
        </div>
      </Section>

      <Section id="history" num="03" title="Medical & Surgical History" meta="eHistory.08">
        <div className="fgrid">
          <div className="fcol-12"><AutoField label="Medical / Surgical History" elem="eHistory.08" /></div>
          <div className="fcol-12">
            <OptionField label="Pregnancy" elem="eHistory.18"
              featured={["3118001", "3118003", "3118011"]} />
          </div>
          <div className="fcol-12"><AutoField label="Barriers to Patient Care" elem="eHistory.01" /></div>
        </div>
      </Section>

      <Section id="allergies" num="04" title="Allergies" meta="eHistory.06 & .07">
        <div className="fgrid">
          <div className="fcol-12">
            <AutoField label="Medication Allergies" elem="eHistory.06" />
          </div>
          <div className="fcol-12">
            <AutoField label="Environmental / Food Allergies" elem="eHistory.07" />
          </div>
        </div>
      </Section>

      <Section id="medications" num="05" title="Current Medications" meta="eHistory.CurrentMedsGroup">
        <CurrentMeds />
      </Section>

      <Section id="belongings" num="06" title="Belongings" meta="ePatient.PatientPropertyGroup">
        <div className="epcr-list-empty">No belongings recorded</div>
        <button className="btn-secondary epcr-list-add" style={{ marginTop: 16 }}>
          <Icons.plus size={14} /> Add item
        </button>
        <div style={{ marginTop: 22 }}>
          <OptionField label="Disposition of Belongings" elem="custom.BelongingsDisposition" path="custom/BelongingsDisposition" />
        </div>
      </Section>

    </ViewLayout>
  );
}

function CurrentMeds() {
  const data = useData();
  const meds = useField("eHistory/eHistory.CurrentMedsGroup");
  const list = Array.isArray(meds) ? meds : [];
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const setMeds = (next) => data.setReportPath("eHistory/eHistory.CurrentMedsGroup", next);

  const addMed = (code) => {
    setMeds([...list, { "eHistory.12": code }]);
    setPickerOpen(false);
  };

  const removeMed = (i) => setMeds(list.filter((_, idx) => idx !== i));

  const existingCodes = new Set(list.map(m => m["eHistory.12"]).filter(Boolean));
  const pickerOptions = (data.options("eHistory.12") || []).filter(o => !existingCodes.has(o.value));

  return (
    <div className="epcr-list">
      {list.map((m, i) => (
        <div key={i} className="epcr-list-row">
          <div className="epcr-list-icon"><Icons.bag size={18} /></div>
          <StaticField label="Medication" value={data.label("eHistory.12", m["eHistory.12"])} nem={m["eHistory.12"]} />
          <button className="icon-btn" onClick={() => removeMed(i)} aria-label="Remove medication">
            <Icons.trash size={16} />
          </button>
        </div>
      ))}
      {list.length === 0 && (
        <div className="epcr-list-empty">No medications recorded</div>
      )}
      <button className="btn-secondary epcr-list-add" onClick={() => setPickerOpen(true)}>
        <Icons.plus size={14} /> Add medication
      </button>
      {pickerOpen && (
        <PickerModal
          title="Add Current Medication"
          elem="eHistory.12"
          options={pickerOptions}
          onClose={() => setPickerOpen(false)}
          onSelect={addMed}
        />
      )}
    </div>
  );
}
